import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  TemplateRef,
  ElementRef,
  ViewChild,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { UiModule } from 'src/app/ui/ui.module';
import { DataSourceConfigOptions, DataSourceService } from 'src/app/services/data-source.service';
import { UserService } from 'src/app/services/user.service';
import { PencilButtonComponent } from 'src/app/standalones/components/pencil-button/pencil-button.component';
import { SearchBarSizeEnum } from 'src/app/ui/search-bar/search-bar.component';
import { changeFiltersToAPIFormat, FiltersService } from 'src/app/services/filters.service';
import { OverlayModule } from '@angular/cdk/overlay';
import { OVERLAY_POSITIONS } from 'src/app/common/utils/overlay-position-constants';
import { GenericFilterComponent } from '../../generic-filters-view/generic-filters-view.component';
import { IconTypes, PencilIconComponent } from '../../pencil-icon/pencil-icon.component';
import { FilterData } from '../../generic-filters-view/filters.interface';
import { AppliedFilterPillsComponent } from '../../applied-filters/applied-filter-pills.component';

export interface SelectionModalSections {
  contentHeader?: TemplateRef<void>;
  content?: TemplateRef<{ $implicit: ContentTemplates }>;
  footer?: TemplateRef<void>;
  disclaimer?: TemplateRef<void>;
}

interface ContentTemplates {
  loading: TemplateRef<void>;
  empty: TemplateRef<void>;
}

/**
 * A generic selection dialog component for selecting entities with search capabilities.
 * Supports customizable content sections with the input `sections`.
 *
 * Not recommended to be used directly, this component is meant to be used as a generic
 * base for all possible types of entity selection dialogs, e.g. `single-selection-list-modal` and
 * `multi-selection-list-modal`.
 *
 * If wanting to use something complete out of the box, please check
 * `single-selection-list-modal` or `multi-selection-list-modal`.
 *
 * @template T The type of entity being displayed/selected in the dialog
 */
@UntilDestroy()
@Component({
  selector: 'app-selection-modal',
  templateUrl: './selection-modal.component.html',
  styleUrls: ['./selection-modal.component.scss'],
  standalone: true,
  providers: [FiltersService],
  imports: [
    CommonModule,
    UiModule,
    OverlayModule,
    PencilButtonComponent,
    PencilIconComponent,
    GenericFilterComponent,
    AppliedFilterPillsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectionModalComponent<T> implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Unique identifier for the dialog. Also used as a prefix for all
   * eligible elements for the `data-name` attribute.
   *
   * As an example, if the `id` value passed was `select-space-dialog`, the close button's
   * `data-name` attribute will be `select-space-dialog-close-button`.
   *
   * @required
   */
  @Input() id!: string;

  /**
   * The title displayed at the top of the dialog.
   *
   * @required
   */
  @Input() title!: string;

  /**
   * Placeholder text shown in the search input field.
   *
   * @required
   */
  @Input() searchPlaceholder!: string;

  /**
   * Configuration object that defines the customizable sections of the generic
   * entity selection dialog by using ng-template elements.
   * Currently, the supported configurable sections are `content`, `contentHeader` and `footer`.
   *
   * `content` is **required**. It's the main section of the dialog,
   * where it's defined how the entity list is going to be displayed.
   * This section provides two different templates in its context to be used
   * by its child components:
   * - `loading`: Template to display when the entity list is being loaded.
   * - `empty`: Template to display when the entity list is empty.
   *
   * `contentHeader` is **optional**. It's the header of the content section and serves to display
   * an important information above the entity list, which could be a title for the list,
   * a message regarding the entries being displayed, the current event's time, etc.
   * If not provided, nothing will be displayed above the entity list.
   *
   * `disclaimer` is **optional**. It's the disclaimer of the dialog, which can be used
   * to display a disclaimer above the entity list, e.g. to display a disclaimer
   * regarding the entries being displayed.
   * If not provided, no disclaimer will be displayed.
   *
   * `footer` is **optional**. It's the footer of the dialog, which can be used
   * to customize the dialog's bottom section actions, e.g. to display a button
   * to close the dialog or to create a new entity.
   * If not provided, no footer will be displayed.
   *
   * @required
   *
   * @example
   * ```html
   * <app-selection-modal
   *   ...
   *   [sections]="{
   *     contentHeader,
   *     content,
   *     footer
   *   }"
   * >
   *   <ng-template #contentHeader>
   *     <h3>Select an entity</h3>
   *   </ng-template>
   *
   *   <ng-template #content let-templates>
   *     <app-entity-list
   *       [loadingTemplate]="templates.loading"
   *       [emptyTemplate]="templates.empty"
   *     ></app-entity-list>
   *   </ng-template>
   *
   *   <ng-template #footer>
   *     <button mat-raised-button color="primary" (click)="closeDialog()">Close</button>
   *   </ng-template>
   * </app-selection-modal>
   * ```
   */
  @Input() sections!: SelectionModalSections;

  /**
   * Defines configuration for how the data will be fetched and handled.
   *
   * @interface DataSourceConfigOptions<T>
   *
   * @property {FetchStrategy} fetchStrategy - The strategy to use for fetching the data, which
   * can be only `server-side` for now.
   * @property {Function} fetch - The function to use for fetching the data. Receives a `FetchDataParams` object
   * as a parameter, which contains the current @see{@link IFilterAPI} parameter, page index and page size;
   * and should return an Observable that emits a `FetchDataResult<T>` object, which contains the items and the total items.
   * This function will mainly be called when the dialog is opened (`ngAfterViewInit`) and whenever the user updates
   * the search query (by clicking on the search button or pressing enter in the search input field).
   * @property {PaginationStrategy} paginationStrategy - The strategy to use for pagination
   * (e.g. `scroll-based` or `page-based`).
   * @property {number} pageSize - The number of items to display per page.
   *
   * @required
   *
   * @example
   * At the component class, you can define the `fetch` function as follows:
   *
   * ```typescript
   * FetchStrategy = FetchStrategy;
   * PaginationStrategy = PaginationStrategy;
   *
   * constructor(...) {
   *   this.fetch = this.fetch.bind(this);
   * }
   *
   * fetch(params: DataSourceFetchParams): Observable<DataSourceFetchResult<T>> {
   *   return this.apiService.getEntities(params).pipe(
   *     map((res) => ({
   *       items: res.items,
   *       totalItems: res.totalItems,
   *     })),
   *   );
   * }
   * ```
   *
   * And at the template, you can pass the `fetch` function as a parameter:
   *
   * ```html
   * <app-selection-modal
   *   ...
   *   [fetchConfig]="{
   *     fetchStrategy: FetchStrategy.ServerSide,
   *     fetch: fetch,
   *     paginationStrategy: PaginationStrategy.ScrollBased,
   *     pageSize: 10
   *   }"
   * >
   * ```
   */
  @Input() fetchConfig!: DataSourceConfigOptions<T>;

  /**
   * Observable that emits the filters to be displayed.
   *
   * Can be easily provided by using `FiltersFactoryService`, e.g.
   * `FiltersFactoryService.createProfilesFilters()`.
   */
  @Input() filters?: Observable<FilterData[]>;

  /**
   * Message to display when no entities are found.
   *
   * If not provided, the default message will be "No data found".
   */
  @Input() emptyMessage?: string;

  /**
   * Event emitter that triggers when the dialog is cancelled.
   *
   * If not subscribed to, the default behavior is that the dialog will close
   * normally when clicked at the close button, with no data being returned.
   */
  @Output() cancel = new EventEmitter<void>();

  @ViewChild('dialogContent', { static: true }) dialogContent!: ElementRef<HTMLElement>;
  @ViewChild('filtersButton') filtersButton?: ElementRef<HTMLElement>;

  SearchBarSizeEnum = SearchBarSizeEnum;
  IconTypes = IconTypes;
  OverlayPositions = OVERLAY_POSITIONS;

  isLoading$ = this.dataSourceService.isLoading$;
  isEmpty$ = this.dataSourceService.isEmpty$;
  appliedFilters$ = this.filtersService.appliedFilters$;

  search = '';
  isRTL = false;
  isFiltersOpen = false;

  constructor(
    private userService: UserService,
    private dialogRef: MatDialogRef<SelectionModalComponent<T>>,
    private dataSourceService: DataSourceService<T>,
    private filtersService: FiltersService,
  ) {}

  ngOnInit(): void {
    this.userService.rtl.pipe(untilDestroyed(this)).subscribe((res) => (this.isRTL = res));

    this.dataSourceService.configure(this.fetchConfig);

    if (this.filters) {
      this.filters.pipe(untilDestroyed(this)).subscribe((filters) => {
        this.filtersService.initFilters(filters);

        this.filtersService.appliedFiltersWithSingleEmpty$
          .pipe(untilDestroyed(this))
          .subscribe(() => this.executeFetch());
      });
    }
  }

  ngAfterViewInit(): void {
    if (!this.filters) {
      this.executeFetch();
    }
  }

  ngOnDestroy(): void {
    this.filtersService.clearFilters();
  }

  executeFetch(reset: boolean = true, resetScroll: boolean = true): void {
    if (reset) {
      this.dataSourceService.reset();
    }
    if (resetScroll) {
      this.scrollContentToTop();
    }

    const filterAPI = changeFiltersToAPIFormat(this.filtersService.appliedFilters) ?? {};
    this.dataSourceService.executeFetch({ ...filterAPI, search: this.search });
  }

  onSearch(searchValue: string): void {
    this.search = searchValue.trim().toLowerCase();
    this.executeFetch();
  }

  onClose(): void {
    this.cancel.observed ? this.cancel.emit() : this.dialogRef.close();
  }

  toggleFiltersPanel(): void {
    this.isFiltersOpen = !this.isFiltersOpen;
  }

  scrollContentToTop(): void {
    this.dialogContent.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
